"use client";

import { useCallback, useSyncExternalStore } from "react";
import type { Editor } from "@tiptap/react";
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    ChevronDown,
    Italic as ItalicIcon,
    List,
    ListOrdered,
    Minus,
    Plus,
    Strikethrough,
    Underline as UnderlineIcon,
    type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
// Same colour picker as lomda V2 (Palette trigger, nice-colors, "Reset (inherit)").
import { ColorPickerControl } from "./controls/ColorPickerControl";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import {
    applyBlockTagToGroup,
    applyAlignToGroup,
    applyDirToGroup,
    nodeTag,
    TAG_CHANGE_META,
    type BlockTag,
} from "../../../lib/groupFormat";
import { FontFamilyControl, fontReadout } from "./controls/FontFamilyControl";
import { refocusEditor } from "./controls/refocusEditor";
import { keepKeysInText } from "../../../hooks/useEditorShortcuts";

const BLOCK_OPTIONS = [
    { value: "label", labelKey: "lomda-v3-editor.format.label" },
    { value: "paragraph", labelKey: "lomda-v3-editor.format.paragraph" },
    { value: "small", labelKey: "lomda-v3-editor.format.small" },
    { value: "h1", labelKey: "lomda-v3-editor.format.h1" },
    { value: "h2", labelKey: "lomda-v3-editor.format.h2" },
    { value: "h3", labelKey: "lomda-v3-editor.format.h3" },
    { value: "h4", labelKey: "lomda-v3-editor.format.h4" },
    { value: "h5", labelKey: "lomda-v3-editor.format.h5" },
    { value: "h6", labelKey: "lomda-v3-editor.format.h6" },
];

/**
 * The textblock the selection currently reads from. For a cursor / text
 * selection `$from.parent` IS that textblock — but a select-all sits at the doc
 * boundary, so `$from.parent` is the doc node (no tag/level) and the readout
 * would fall back to "h1"/"paragraph". Descend to the first block it covers so
 * the toolbar still shows the real element (fixes Ctrl+A reading "h1").
 */
function currentBlockNode(editor: Editor) {
    const { $from } = editor.state.selection;
    if ($from.parent.isTextblock) return $from.parent;
    return $from.nodeAfter ?? $from.parent;
}

/**
 * A string encoding every editor-derived value the toolbar shows (current block
 * tag, marks, align, dir, font, colour). Deterministic for a given selection —
 * so `useSyncExternalStore` re-renders only when something actually changes.
 */
function editorSnapshot(editor: Editor): string {
    const node = currentBlockNode(editor);
    const heading =
        node.type.name === "heading" ? `h${node.attrs.level ?? 1}` : "";
    const align =
        ["left", "center", "right"].find((a) =>
            editor.isActive({ textAlign: a })
        ) ?? "";
    const ts = editor.getAttributes("textStyle");
    const font = fontReadout(editor);
    const flags = [
        "bold",
        "italic",
        "underline",
        "strike",
        "bulletList",
        "orderedList",
        "table",
    ]
        .map((m) => (editor.isActive(m) ? "1" : "0"))
        .join("");
    return [
        heading,
        node.attrs.small ? "s" : "",
        node.attrs.label ? "l" : "",
        (node.attrs.dir as string) ?? "",
        align,
        `${font.ownFont}:${font.group}`,
        (ts.color as string) ?? "",
        flags,
    ].join("|");
}

/**
 * Re-render the toolbar whenever the active editor's selection/marks change,
 * with NO missed-event race. `useSyncExternalStore` re-reads the snapshot right
 * after (re)subscribing, so a focus/selection change landing in the gap between
 * render and subscribe can't leave the readout stale — this fixes the
 * intermittent "cursor on a title but it says paragraph" bug.
 */
function useEditorSnapshot(editor: Editor) {
    const subscribe = useCallback(
        (onChange: () => void) => {
            editor.on("selectionUpdate", onChange);
            editor.on("transaction", onChange);
            editor.on("focus", onChange);
            editor.on("blur", onChange);
            return () => {
                editor.off("selectionUpdate", onChange);
                editor.off("transaction", onChange);
                editor.off("focus", onChange);
                editor.off("blur", onChange);
            };
        },
        [editor]
    );
    const getSnapshot = useCallback(() => editorSnapshot(editor), [editor]);
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Platform-themed ghost format button (matches lomda V2): 32px, active = muted. */
function FmtBtn({
    label,
    active,
    onClick,
    children,
}: {
    label: string;
    active?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={label}
            aria-pressed={active}
            title={label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            className={cn(
                "h-8 w-8 shrink-0",
                // Same token the hover uses, so an active button and a hovered
                // one aren't two different blues.
                active && "bg-accent text-accent-foreground"
            )}
        >
            {children}
        </Button>
    );
}

/** A multi-option control collapsed to one trigger (current option + chevron)
 *  that opens a dropdown of the rest — same as lomda V2's align/direction. */
function OptionDropdown<T extends string>({
    options,
    value,
    onChange,
    ariaLabel,
    dir,
    onCloseAutoFocus,
}: {
    options: ReadonlyArray<{
        value: T;
        label: string;
        Icon?: LucideIcon;
        short?: string;
    }>;
    value: T;
    onChange: (value: T) => void;
    ariaLabel: string;
    dir: "ltr" | "rtl";
    onCloseAutoFocus: (e: Event) => void;
}) {
    const current = options.find((o) => o.value === value) ?? options[0];
    const TriggerIcon = current.Icon;
    return (
        <DropdownMenu dir={dir}>
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 gap-0.5 px-1.5"
                    aria-label={ariaLabel}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {TriggerIcon ? (
                        <TriggerIcon className="size-4" />
                    ) : (
                        <span className="text-xs">{current.short}</span>
                    )}
                    <ChevronDown className="size-3 opacity-50" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                onCloseAutoFocus={onCloseAutoFocus}
            >
                {/* Radio items, not a background on the current one: the menu's
                    own highlight is `accent`, so a second shade for "selected"
                    put two near-identical blues on screen at once. */}
                <DropdownMenuRadioGroup
                    value={value}
                    onValueChange={(v) => onChange(v as T)}
                >
                    {options.map((o) => {
                        const ItemIcon = o.Icon;
                        return (
                            <DropdownMenuRadioItem key={o.value} value={o.value}>
                                {ItemIcon ? (
                                    <ItemIcon className="size-4 me-2" />
                                ) : (
                                    <span className="me-2 w-4 text-center text-xs">
                                        {o.short}
                                    </span>
                                )}
                                {o.label}
                            </DropdownMenuRadioItem>
                        );
                    })}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function RichTextToolbar({
    editor,
    inline,
}: {
    editor: Editor;
    inline?: boolean;
}) {
    useEditorSnapshot(editor);
    const { dir } = useLocale();

    const activeGroupKey = useLomdaV3Editor((s) => s.activeGroupKey);

    const node = currentBlockNode(editor);
    const blockValue = nodeTag(node) ?? "paragraph";
    const docDir = dir === "rtl" ? "rtl" : "ltr";
    const curDir = (node.attrs.dir as "ltr" | "rtl" | undefined) ?? docDir;
    const explicitAlign = ["left", "center", "right"].find((a) =>
        editor.isActive({ textAlign: a })
    ) as "left" | "center" | "right" | undefined;
    const curAlign = explicitAlign ?? (docDir === "rtl" ? "right" : "left");
    const curColor = (editor.getAttributes("textStyle").color as string) || "";
    const inTable = editor.isActive("table");

    const setBlock = (v: string) => {
        if (v === "paragraph") {
            editor
                .chain()
                .focus()
                .setParagraph()
                .updateAttributes("paragraph", { label: false, small: false })
                .setMeta(TAG_CHANGE_META, true)
                .run();
        } else if (v === "label") {
            editor
                .chain()
                .focus()
                .setParagraph()
                .updateAttributes("paragraph", { label: true, small: false })
                .setMeta(TAG_CHANGE_META, true)
                .run();
        } else if (v === "small") {
            editor
                .chain()
                .focus()
                .setParagraph()
                .updateAttributes("paragraph", { small: true, label: false })
                .setMeta(TAG_CHANGE_META, true)
                .run();
        } else {
            const level = parseInt(v.replace("h", ""), 10) as
                | 1
                | 2
                | 3
                | 4
                | 5
                | 6;
            editor
                .chain()
                .focus()
                .toggleHeading({ level })
                .setMeta(TAG_CHANGE_META, true)
                .run();
        }
        applyBlockTagToGroup(activeGroupKey, v as BlockTag, editor);
    };

    const setAlign = (align: "left" | "center" | "right") => {
        editor.chain().focus().setTextAlign(align).run();
        applyAlignToGroup(activeGroupKey, align, editor);
    };
    const setDir = (d: "ltr" | "rtl") => {
        editor.chain().focus().setBlockDirection(d).run();
        applyDirToGroup(activeGroupKey, d, editor);
    };

    const TextAlignStartIcon = docDir === "rtl" ? AlignRight : AlignLeft;
    const TextAlignEndIcon = docDir === "rtl" ? AlignLeft : AlignRight;

    return (
        <div
            onKeyDownCapture={keepKeysInText}
            className={cn(
                "flex items-center gap-1 lomda-v3-chrome text-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                inline
                    ? "min-w-0 overflow-x-auto"
                    : "absolute -top-12 start-0 z-30 max-w-[calc(100vw-2rem)] overflow-x-auto rounded-xl border bg-card p-2 shadow-md"
            )}
        >
            {/* Text tag */}
            <Select value={blockValue} onValueChange={setBlock} dir={dir}>
                <SelectTrigger className="w-[120px] h-8 shrink-0 gap-1">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent onCloseAutoFocus={refocusEditor(editor)}>
                    {BLOCK_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {t(o.labelKey)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Bold / Italic / Underline / Strike */}
            <FmtBtn
                label={t("lomda-v3-editor.format.bold")}
                active={editor.isActive("bold")}
                onClick={() => editor.chain().focus().toggleBold().run()}
            >
                <span className="text-sm font-bold">B</span>
            </FmtBtn>
            <FmtBtn
                label={t("lomda-v3-editor.format.italic")}
                active={editor.isActive("italic")}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            >
                <ItalicIcon className="size-4" />
            </FmtBtn>
            <FmtBtn
                label={t("lomda-v3-editor.format.underline")}
                active={editor.isActive("underline")}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
                <UnderlineIcon className="size-4" />
            </FmtBtn>
            <FmtBtn
                label={t("lomda-v3-editor.format.strike")}
                active={editor.isActive("strike")}
                onClick={() => editor.chain().focus().toggleStrike().run()}
            >
                <Strikethrough className="size-4" />
            </FmtBtn>

            {/* Color (same control as V2) */}
            <ColorPickerControl editor={editor} textColor={curColor} />

            {/* Font (shared FontSelector) */}
            <FontFamilyControl editor={editor} />

            {/* Lists */}
            <FmtBtn
                label={t("lomda-v3-editor.format.list-bulleted")}
                active={editor.isActive("bulletList")}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
                <List className="size-4" />
            </FmtBtn>
            <FmtBtn
                label={t("lomda-v3-editor.format.list-numbered")}
                active={editor.isActive("orderedList")}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
                <ListOrdered className="size-4" />
            </FmtBtn>

            {/* Alignment */}
            <OptionDropdown
                dir={dir}
                onCloseAutoFocus={refocusEditor(editor)}
                ariaLabel={t("lomda-v3-editor.format.align-center")}
                value={curAlign}
                onChange={setAlign}
                options={[
                    {
                        value: "left",
                        Icon: TextAlignStartIcon,
                        label: t("lomda-v3-editor.format.align-start"),
                    },
                    {
                        value: "center",
                        Icon: AlignCenter,
                        label: t("lomda-v3-editor.format.align-center"),
                    },
                    {
                        value: "right",
                        Icon: TextAlignEndIcon,
                        label: t("lomda-v3-editor.format.align-end"),
                    },
                ]}
            />

            {/* Direction */}
            <OptionDropdown
                dir={dir}
                onCloseAutoFocus={refocusEditor(editor)}
                ariaLabel={t("lomda-v3-editor.format.ltr")}
                value={curDir}
                onChange={setDir}
                options={[
                    {
                        value: "ltr",
                        short: "LTR",
                        label: t("toolbar.dir-ltr"),
                    },
                    {
                        value: "rtl",
                        short: "RTL",
                        label: t("toolbar.dir-rtl"),
                    },
                ]}
            />

            {/* Table controls (contextual) */}
            {inTable && (
                <div className="flex shrink-0 items-center gap-0.5">
                    <span className="px-1 text-xs text-muted-foreground">
                        {t("lomda-v3-editor.text.rows")}
                    </span>
                    <FmtBtn
                        label={t("lomda-v3-editor.format.table-del-row")}
                        onClick={() => editor.chain().focus().deleteRow().run()}
                    >
                        <Minus className="size-4" />
                    </FmtBtn>
                    <FmtBtn
                        label={t("lomda-v3-editor.format.table-add-row")}
                        onClick={() =>
                            editor.chain().focus().addRowAfter().run()
                        }
                    >
                        <Plus className="size-4" />
                    </FmtBtn>
                    <span className="px-1 text-xs text-muted-foreground">
                        {t("lomda-v3-editor.text.columns")}
                    </span>
                    <FmtBtn
                        label={t("lomda-v3-editor.format.table-del-col")}
                        onClick={() =>
                            editor.chain().focus().deleteColumn().run()
                        }
                    >
                        <Minus className="size-4" />
                    </FmtBtn>
                    <FmtBtn
                        label={t("lomda-v3-editor.format.table-add-col")}
                        onClick={() =>
                            editor.chain().focus().addColumnAfter().run()
                        }
                    >
                        <Plus className="size-4" />
                    </FmtBtn>
                </div>
            )}

        </div>
    );
}
